// No persistent selection cache. Each page entry validates its publication and
// revalidates the selected bytes; only that decoded photograph can be displayed.
export async function requestHeroMedia(slot, signal) {
  const response = await fetch(`/api/site/v1/hero-media/${encodeURIComponent(slot)}`, {
    credentials: 'omit', cache: 'no-store', redirect: 'error', signal,
  });
  const result = await response.json();
  if (!response.ok) throw new Error('The published image could not be read.');
  const media = result.data;
  if (!media || media.slot !== slot || !['ready', 'unconfigured', 'none', 'error'].includes(media.status))
    throw new Error('Invalid published image response.');
  if (media.status === 'ready' && (!media.imageUrl || !media.imageAlt || !media.assetId || !/^[a-f0-9]{64}$/.test(media.contentSha256 || '') || !(media.publicationRevision > 0) || media.bytePolicy !== 'revalidate'))
    throw new Error('Incomplete published image identity.');
  return media;
}

export async function decodeSelectedMedia(media, signal) {
  // reload bypasses stored bytes even when an old public URL is mutable. Blob
  // URLs pin exactly the bytes validated on this visit without provider rewrites.
  // Only published canonical selections use the byte route. Admin draft/library
  // candidates lack this identity and retain their existing direct preview path.
  const publishedExternal = /^https:\/\//.test(media.imageUrl || '') && media.status === 'ready'
    && /^[A-Za-z][A-Za-z0-9]*$/.test(media.slot || '') && /^[a-f0-9]{64}$/.test(media.assetId || '')
    && media.publicationRevision > 0 && media.bytePolicy === 'revalidate';
  const byteUrl = publishedExternal
    ? `/api/site/v1/hero-media/${encodeURIComponent(media.slot)}/bytes?assetId=${media.assetId}` : media.imageUrl;
  const response = await fetch(byteUrl, { credentials: 'omit', cache: 'reload', redirect: 'error', signal });
  if (!response.ok || !/^image\//i.test(response.headers.get('content-type') || ''))
    throw new Error('The selected image is unavailable. Choose a public image in the media library.');
  const blob = await response.blob();
  if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
  const bytes = blob.arrayBuffer ? await blob.arrayBuffer() : await new Promise((resolve, reject) => {
    const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = reject; reader.readAsArrayBuffer(blob);
  });
  const digest = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), byte => byte.toString(16).padStart(2, '0')).join('');
  if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
  if (media.contentSha256 && media.contentSha256 !== digest) throw new Error('The selected image bytes changed. Reload and republish the intended image in Admin.');
  const url = URL.createObjectURL(blob);
  try {
    await new Promise((resolve, reject) => {
      const img = new Image();
      const done = (error) => {
        signal?.removeEventListener('abort', abort);
        img.onload = null; img.onerror = null;
        error ? reject(error) : resolve();
      };
      const abort = () => done(new DOMException('Cancelled', 'AbortError'));
      signal?.addEventListener('abort', abort, { once: true });
      img.onerror = () => done(new Error('The selected image could not be decoded.'));
      img.onload = () => {
        if (!img.decode) done();
        else img.decode().then(() => done(), () => done(new Error('The selected image could not be decoded.')));
      };
      img.src = url;
    });
    if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    return { ...media, contentSha256: digest, displayUrl: url };
  } catch (error) { URL.revokeObjectURL(url); throw error; }
}

// Active leases coalesce StrictMode's setup/cleanup/setup only. A new navigation
// receives a new lease and never exposes a photograph from a prior entry.
const visits = new Map();
export function acquireHeroVisit(key, slot) {
  let entry = visits.get(key);
  if (!entry) {
    const controller = new AbortController();
    entry = { refs: 0, controller };
    const timer = setTimeout(() => controller.abort(), 20000);
    entry.promise = requestHeroMedia(slot, controller.signal)
      .then(media => media.status === 'ready' ? decodeSelectedMedia(media, controller.signal) : media)
      .then(media => { entry.media = media; return media; })
      .finally(() => clearTimeout(timer));
    visits.set(key, entry);
  }
  entry.refs += 1;
  return { promise: entry.promise, release() {
    entry.refs -= 1;
    queueMicrotask(() => {
      if (entry.refs || visits.get(key) !== entry) return;
      visits.delete(key); entry.controller.abort();
      if (entry.media?.displayUrl) URL.revokeObjectURL(entry.media.displayUrl);
    });
  } };
}
